import { namespace } from 'wsm-tagging-utils';

const runDataMapper = (win) => {
	namespace(win, 'DDC.tracking.adobe_launch.util');

	const {
		DDC: { tracking }
	} = win;

	/**
	 * Utility class to handle all data transformations and lookups for
	 * FD tracking events.
	 *
	 * @param {Object} win (optional) an instance of the global window object
	 * @constructor
	 */
	tracking.adobe_launch.util.DataMapper =
		tracking.adobe_launch.util.DataMapper ||
		(function buildDataMapper() {
			// ======================================================================
			// ------------------ Private Variables and Functions -------------------
			// ======================================================================

			/**
			 * Returns the value of the object property located at the provided path.
			 *
			 * @param {Object} obj The object to traverse for a desired property
			 * @param {string} path The path to the desired object property
			 * @returns {*} The value of the property at the given path
			 */
			function traverseObject(objIn, path) {
				let obj = objIn;
				if (typeof obj === 'object' && typeof path === 'string') {
					const levels = path.split('.');

					while (obj && levels.length) {
						obj = obj[levels.shift()];
					}
				}

				return obj;
			}

			class DataMapper {
				constructor(thisWin = window) {
					this.win = thisWin;

					if (
						thisWin?.DDC?.WidgetData?.['ws-tagging']?.mapping
							?.adobe_launch
					) {
						this.setPageAliasMapping(
							thisWin.DDC.WidgetData['ws-tagging'].mapping
								.adobe_launch.pageAlias
						);
						this.setFormTrackingIdMapping(
							thisWin.DDC.WidgetData['ws-tagging'].mapping
								.adobe_launch.form
						);
						this.setGTMMapping(
							thisWin.DDC.WidgetData['ws-tagging'].mapping
								.adobe_launch.gtm
						);
					}

					/**
					 * General mapping object for direct key-value pair translations.
					 */
					this.mapping = {
						// DDC Client Device to GM Adobe 'renderedExperience'
						// ex. <DDC client type>: 'desired type name'
						clientDevice: {
							desktop: 'large',
							mobile: 'small',
							tablet: 'medium',
							default: 'large'
						},

						// ex. <DDC formTrackingId>: 'desired form name'
						formTrackingId: {},

						// ex. <DDC inventory type>: 'desired type name'
						inventoryType: {
							1: 'used',
							2: 'new',
							3: 'cpo',
							new: 'new',
							used: 'used',
							certified: 'cpo',
							all: 'all',
							default: 'all'
						},

						franchise: {
							ch: {
								buick: 'bu',
								cadillac: 'ca',
								chevrolet: 'ch',
								gmc: 'gm',
								nissan: 'na'
							},
							full: {
								buick: 'buick',
								cadillac: 'cadillac',
								chevrolet: 'chevrolet',
								gmc: 'gmc',
								nissan: 'nissan'
							}
						},

						orientation: {
							'landscape-primary': 'landscape',
							'landscape-secondary': 'landscape',
							'portrait-primary': 'portrait',
							'portrait-secondary': 'portrait'
						}
					};
				}

				// ======================================================================
				// ------------------------ Getters and Setters -------------------------
				// ======================================================================

				/**
				 * @returns {Object} The map of direct key-value pair translations.
				 */
				getMapping() {
					return this.mapping;
				}

				/**
				 * Adds new or modifies existing key-value pair mappings.
				 *
				 * @param {Object} newMapping The object containing key-value pair mappings.
				 *	Object can contain nested objects.
				 */
				setMapping(newMapping) {
					if (typeof newMapping === 'object') {
						this.mapping = this.mergeObjects(
							this.mapping,
							newMapping
						);
					}
				}

				/**
				 * @returns {Object} The map of properties by form tracking id.
				 */
				getFormTrackingIdMapping() {
					return this.formTrackingIdMapping;
				}

				/**
				 * Adds a new form tracking id key value pair to the formTrackingIdMapping Class Object
				 *
				 * @param {Object} newFormTrackingIdMapping The form tracking mapping object with
				 * 	additional form tracking ids to add to handler's formTrackingIdMapping object
				 */
				setFormTrackingIdMapping(newFormTrackingIdMapping) {
					if (typeof newFormTrackingIdMapping === 'object') {
						this.formTrackingIdMapping = this.mergeObjects(
							this.formTrackingIdMapping,
							newFormTrackingIdMapping
						);
					}
				}

				/**
				 * @returns {Object} The map of properties by page alias.
				 */
				getPageAliasMapping() {
					return this.pageAliasMapping;
				}

				/**
				 * Adds a new page alias key value pair to the pageAliasMapping Class Object
				 *
				 * @param {Object} newPageAliasMapping The page alias mapping object with
				 * 	additional aliases to add to handler's pageAliasMapping object
				 */
				setPageAliasMapping(newPageAliasMapping) {
					if (typeof newPageAliasMapping === 'object') {
						this.pageAliasMapping = this.mergeObjects(
							this.pageAliasMapping,
							newPageAliasMapping
						);
					}
				}

				/**
				 * @returns {Object} The gtm's map of events and properties to match.
				 */
				getGTMMapping() {
					return this.gtmMapping;
				}

				/**
				 * Adds a new events key value pair to the GtmEventsMapping Class Object
				 *
				 * @param {Object} GtmEventsMapping The events mapping object with
				 * 	additional properties to match and add to handler's GtmEventsMapping object
				 */
				setGTMMapping(gtmMapping) {
					if (typeof gtmMapping === 'object') {
						this.gtmMapping = this.mergeObjects(
							this.gtmMapping,
							gtmMapping
						);
					}
				}

				// ======================================================================
				// --------------------------- Utilities --------------------------------
				// ======================================================================

				/**
				 * Returns the value of an object property located in the global namespace.
				 *
				 * @param {string} path The path to the desired property from window
				 * @returns {*} The value of the property at the given path or undefined
				 */
				lookup(path) {
					return traverseObject(this.win, path);
				}

				/**
				 * Merges two objects.
				 *
				 * @param {Object} obj1 The object that will be modified
				 * @param {Object} obj2 The object to be merged in
				 * @returns {Object} The merged object
				 */
				mergeObjects(obj1In, obj2) {
					let obj1 = obj1In;

					// this conditional is to address all window objects being pushed into this function since it is circular.
					// I did not state window for testing purposes but just know
					// You can read this the same as window.self === window
					if (obj2?.self === obj2) {
						return obj1;
					}

					if (typeof obj2 === 'object' && obj2 !== null) {
						if (typeof obj1 !== 'object') {
							obj1 = {};
						}

						Object.keys(obj2).forEach((key) => {
							obj1[key] = this.mergeObjects(obj1[key], obj2[key]);
						});
					} else {
						obj1 = obj2;
					}

					return obj1;
				}

				// ======================================================================
				// ----------------------------- Mappers --------------------------------
				// ======================================================================

				/**
				 * Returns the value of an object property located at the provided path.
				 *
				 * @param {string} path The path to the desired property from window
				 * @returns {*} The value of the mapped property, a default mapping, or
				 *	the mapping key if not found
				 */
				map(path) {
					let mappedVal;

					if (typeof path === 'string') {
						const levels = path.split('.');

						mappedVal =
							traverseObject(this.mapping, path) ||
							traverseObject(
								this.mapping,
								`${
									levels[levels.length - 2] || levels[0]
								}.default`
							) ||
							levels[levels.length - 1] ||
							levels[0];
					}

					return mappedVal;
				}

				/**
				 * Returns the franchise code mapping given the sites franchise list.
				 * The default return is an empty string
				 *
				 * @param {Array} franchises The list of franchises associated with the site
				 * @param {boolean} useCode true for 2-character franchise code, false for full value
				 * @returns {string} The franchise code mapping
				 */
				mapFranchises(franchises, useCode) {
					if (Array.isArray(franchises)) {
						const valType = useCode ? 'ch' : 'full';
						return franchises
							.map((val) =>
								this.map(`franchise.${valType}.${val}`)
							)
							.join('|');
					}

					return '';
				}

				/**
				 * Returns the gtm event name given the source event information.
				 *
				 * @param {string} sourceUniqueEvent
				 * @param {string} sourceEvent
				 * @param {string} sourceFormName
				 * @returns {string} The gtm name event
				 *
				 * If sourceUniqueEvent is in the map return the map
				 * If no formName in the map, or the sourceFormName found in the map then...
				 *  check for matching event in the map and if found return the key
				 *
				 *  Mappings live in the ws-tagging OSIRIS widget
				 */
				mapGTMs(
					sourceUniqueEvent = '',
					sourceEvent = '',
					sourceFormName = ''
				) {
					const key = Object.entries(this.gtmMapping).find(
						([, gtmEventInfo]) => {
							const { event, formName, uniqueEvent } =
								gtmEventInfo;

							if (uniqueEvent === sourceUniqueEvent) {
								return true;
							}

							const formNameExists = formName !== undefined;
							const formNameMatch =
								formNameExists && formName === sourceFormName;
							const formNameIncludes =
								formNameExists &&
								Object.values(formName).includes(
									sourceFormName
								);

							if (uniqueEvent === undefined) {
								if (
									!formNameExists ||
									formNameMatch ||
									formNameIncludes
								) {
									if (
										event &&
										(event === sourceEvent ||
											Object.values(event).includes(
												sourceEvent
											))
									) {
										return true;
									}
								}
							}

							return false;
						}
					);

					return key?.[0];
				}

				/**
				 * Parses params string for the value associated with a given key.
				 * @param {string} name The name of the key.
				 * @param {string} params Parameters we want to evaluate.
				 * @returns {string} The value of the key.
				 */
				getParamStringValue = function getUrlParamStringValue(
					name,
					params
				) {
					let val = '';

					if (name) {
						const paramNameAndValue =
							typeof params === 'string' &&
							params
								.split('&')
								.find((item) => item.indexOf(name) === 0);

						if (paramNameAndValue) {
							// index 0 is the name
							// index 1 is the value
							val = paramNameAndValue.split('=')[1] || val;
						}
					}

					return val;
				};
			}

			return DataMapper;
		})();
	/**
	 * DDC Page Alias to property mapping; pages with a
	 * matching alias will have its properties applied to the
	 * data payload. NOTE: vehicle nameplates will automatically
	 * be appended to pageNames
	 * ex. 'PAGE_ALIAS_REGEX': { pageName: 'desired page name' }
	 */
	tracking.adobe_launch.util.DataMapper.prototype.pageAliasMapping = {};
	tracking.adobe_launch.util.DataMapper.prototype.gtmMapping = {};
	tracking.adobe_launch.util.DataMapper.prototype.formTrackingIdMapping = {};
};

export default runDataMapper;
